
/* eslint-disable @typescript-eslint/camelcase */
import {
  defineComponent,
  ref,
  reactive,
  computed,
  onMounted,
  watch
} from "vue";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import SidebarItem from "./SidebarItem.vue";
import { AccountController } from "../../controllers/AccountController";
import { ConfigSidebar } from "@/domain/entities/MainApp";
import { debounce } from "lodash";
import router from "../../router";
import { MainAppController } from "../../controllers/MainAppController";
import { FlagsSidebar } from "@/feature-flags/flags-sidebar";
import {
  checkRolePermission,
  IS_SENDER_ACCOUNT
} from "@/app/infrastructures/misc/Utils";
import { fcsFlagClient } from "@/feature-flags/unleash";
import Rox from "rox-browser";
import CardSisaSaldo from "./CardSisaSaldo.vue";
import CardSisaSaldoCod from "./CardSisaSaldoCod.vue";
import CardReview from "./CardReview.vue";
import ModalFeedbackReview from "../../components/modal-feedback-review/index.vue";

export default defineComponent({
  name: "SidebarV2",
  components: {
    SidebarItem,
    Accordion,
    AccordionTab,
    CardSisaSaldo,
    CardReview,
    ModalFeedbackReview,
    CardSisaSaldoCod
  },
  setup() {
    const isCollapsed = ref(MainAppController.screenSize.width <= 425);
    const expandMenu = reactive<Record<string, boolean>>({});
    const active = ref(router.currentRoute.value.path);
    const activeIndexMenu = ref<any>([]);
    const search = ref("");
    const menu = ref<ConfigSidebar[]>([]);
    const op = ref<any>(null);
    const activeName = ref("");
    const whitelistDeeplink = [
      "Booking Retail",
      "Booking Ritel",
      "Booking Aplikasi & Marketplace",
      "Booking Korporat (CBP)"
    ];

    const isMatchRoute = (path: string, to: string) => {
      if (!to) return false;
      if (to.startsWith("__")) return path === to;
      return path === to || path.startsWith(`${to}/`);
    };

    const isActiveChild = (to: string, label: string) => {
      if (whitelistDeeplink.includes(label)) {
        return active.value === to && label === activeName.value;
      }
      if (activeName.value) {
        return activeName.value === label && isMatchRoute(active.value, to);
      }
      return isMatchRoute(active.value, to);
    };

    let listMenu: ConfigSidebar[] = [];

    const checkPermission = (item: ConfigSidebar): boolean => {
      const hasPermissionLegacy =
        !!item.permission_legacy?.length &&
        item.permission_legacy.some(checkRolePermission);

      const hasPermissionFlags =
        !!item.feature_flag?.length &&
        item.feature_flag.some(
          (e) =>
            Number(fcsFlagClient.getVariant(e).payload?.value) ||
            Rox.flags.find((val) => val.name === e)?.isEnabled()
        );

      return hasPermissionLegacy || hasPermissionFlags;
    };

    /** ========= COMPUTED ========= */
    const accountData = computed(() => AccountController.accountData);
    const isCustomerService = computed(
      () => accountData.value.account_type === "customer-service"
    );

    const logoSource = computed(() => {
      const {
        account_type,
        account_type_detail,
        role_type
      } = accountData.value;

      if (!account_type && !account_type_detail?.type) return "";

      if (account_type === "client") return role_type;

      if (account_type === "internal") return "general";
      return account_type.toLowerCase() === "partner"
        ? account_type_detail.type
        : account_type;
    });

    const flagsMenu = computed(() => {
      const val = FlagsSidebar.config_sidebar_menu.getValue();
      const items = val?.featureSidebar?.config_sidebar_menu || [];
      return items
        .map((e: any) => new ConfigSidebar(e))
        .filter(checkPermission)
        .map(
          (e: ConfigSidebar) =>
            new ConfigSidebar({
              ...e,
              children: e.children?.filter(checkPermission)
            })
        );
    });

    /** ========= HELPERS ========= */
    const getItemKey = (item: ConfigSidebar) => item.to || `__${item.label}__`;
    const toggleExpand = (item: ConfigSidebar) => {
      const key = getItemKey(item); // fallback ke label biar unik
      expandMenu[key] = !expandMenu[key];
    };

    const resetExpand = () => {
      listMenu.forEach((item) => (expandMenu[getItemKey(item)] = false));
    };

    const expandDefaultMenus = () => {
      const defaults = listMenu
        .map((e, i) => (e.is_default_expand ? (toggleExpand(e), i) : null))
        .filter((i): i is number => i !== null);

      activeIndexMenu.value = defaults;
      menu.value = listMenu;
    };

    /** ========= ACTIONS ========= */

    const setActive = (key: string, name = "") => {
      active.value = key;
      activeName.value = name;

      // kalau key aslinya route → push ke router
      if (!key.startsWith("__")) {
        router.push(key);
      }
    };

    const onClickParentMenu = (item: ConfigSidebar) => {
      const key = getItemKey(item);

      return item.children?.length ? toggleExpand(item) : setActive(key);
    };

    const doSearch = (value: string) => {
      search.value = value;
      activeIndexMenu.value = [];
      resetExpand();

      if (!value) {
        expandDefaultMenus();
        return;
      }

      const lower = value.toLowerCase();
      menu.value = listMenu
        .map((item) => {
          if (item.label.toLowerCase().includes(lower)) return item;

          const children = item.children?.filter((c) =>
            c.label.toLowerCase().includes(lower)
          );
          return children?.length ? { ...item, children } : null;
        })
        .filter(Boolean) as ConfigSidebar[];

      menu.value.forEach((item, i) => {
        if (item.children?.length) {
          activeIndexMenu.value.push(i);
          expandMenu[item.to] = true;
        }
      });
    };

    const onSearch = debounce(
      (e: Event) =>
        doSearch((e.target as HTMLInputElement).value.toLowerCase()),
      300
    );

    const clearSearch = () => {
      search.value = "";
      doSearch("");
    };

    const isActiveHeader = (item: ConfigSidebar) => {
      if (item.is_default_expand === false && item.children?.length)
        return false;

      const key = getItemKey(item);

      if (item.to) {
        // avoid substring match: "/x-v2" should not activate "/x"
        return (
          isMatchRoute(active.value, key) ||
          item.children?.some((c) => isMatchRoute(active.value, getItemKey(c)))
        );
      }

      // untuk key buatan (to kosong), harus match persis
      return active.value === key;
    };

    const toggleCollapsed = () => {
      isCollapsed.value = !isCollapsed.value;
      resetExpand();

      if (isCollapsed.value) {
        activeIndexMenu.value = [];
        return;
      }
      expandDefaultMenus();
    };

    const accountDivisionName = computed(() => accountData.value.account_type_detail.divisionName);
    const getDivisionName = computed(() => {
      if (accountDivisionName.value === "Operation Analyst") {
        return "Ops Analyst"
      } else if (accountDivisionName.value === "Operation Cost & Productivity") {
        return "Ops Cost & Productivity";
      }
      return accountDivisionName.value;
    })

    /** ========= LIFECYCLE ========= */
    onMounted(() => {
      watch(
        () => flagsMenu.value,
        (val) => {
          const selected = val.reduce(
            (acc: number[], e: ConfigSidebar, i: number) => {
              if (
                e.to === active.value ||
                e.children?.some((c: ConfigSidebar) => c.to === active.value) ||
                e.is_default_expand
              ) {
                if (e.children?.length) toggleExpand(e);
                acc.push(i);
              }
              return acc;
            },
            []
          );

          activeIndexMenu.value.push(...selected);
          listMenu = menu.value = val;
        },
        { immediate: true }
      );
      watch(
        () => router.currentRoute.value.meta?.name,
        (newName) => {
          if (newName === "Pusat Resolusi" || newName === "Notifikasi") {
            active.value = "";
          } else {
            active.value = router.currentRoute.value.path;
          }
        },
        { immediate: true }
      );
    });

    /** ========= TOOLTIP & SUBMENU ========= */
    const selectSubMenu = (item: ConfigSidebar, close: () => void) => {
      setActive(item.to);
      close();
    };

    const openTooltip = (e: Event) => op.value?.toggle(e);

    const onClickSisaSaldo = () => {
      activeIndexMenu.value = [];
      resetExpand();
    };

    const onClickSisaSaldoCod = () => {
      activeIndexMenu.value = [];
      resetExpand();
    };

    const permissionToggleVersionSidebarEnabled = computed(() => {
      return (
        FlagsSidebar.permission_toggle_version_sidebar_enabled.isEnabled() &&
        !IS_SENDER_ACCOUNT &&
        !isCollapsed.value
      );
    });

    /** ========= RETURN ========= */
    return {
      isCollapsed,
      expandMenu,
      activeIndexMenu,
      active,
      accountData,
      isCustomerService,
      logoSource,
      menu,
      search,
      onSearch,
      permissionToggleVersionSidebarEnabled,
      getItemKey,
      clearSearch,
      op,
      doSearch,
      toggleCollapsed,
      toggleExpand,
      setActive,
      onClickParentMenu,
      isActiveHeader,
      selectSubMenu,
      openTooltip,
      prevVersion: () => MainAppController.setSidebarAndBreadcrumbV2(false),
      onClickSisaSaldo,
      onClickSisaSaldoCod,
      isActiveChild,
      accountDivisionName,
      getDivisionName
    };
  }
});
